"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import {
  Language,
  SUPPORTED_LANGUAGES,
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  LanguageOption,
  TranslationParams,
} from "./types";
import { locales, Translations, en } from "@/locales";

export interface LanguageContextType {
  language: Language;
  languages: LanguageOption[];
  setLanguage: (lang: Language) => void;
  t: (key: string, params?: TranslationParams) => string;
  isRomanian: boolean;
  isEnglish: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function getNestedValue(obj: any, path: string): string | undefined {
  if (!obj || !path) return undefined;
  const parts = path.split(".");
  let current: any = obj;
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    current = current[part];
  }
  return typeof current === "string" ? current : undefined;
}

function interpolate(template: string, params?: TranslationParams): string {
  if (!params || !template) return template;
  let result = template;
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    const strVal = String(value);
    // Replace {key} and {{key}}
    result = result
      .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, "g"), strVal)
      .replace(new RegExp(`\\{${key}\\}`, "g"), strVal);
  }
  return result;
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(DEFAULT_LANGUAGE);
  const [mounted, setMounted] = useState(false);

  const applyLanguageToDOM = useCallback((lang: Language) => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("lang", lang);
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null;
      if (stored === "en" || stored === "ro") {
        setLanguageState(stored);
        applyLanguageToDOM(stored);
      } else if (typeof navigator !== "undefined" && navigator.language?.toLowerCase().startsWith("ro")) {
        setLanguageState("ro");
        applyLanguageToDOM("ro");
      } else {
        setLanguageState(DEFAULT_LANGUAGE);
        applyLanguageToDOM(DEFAULT_LANGUAGE);
      }
    } catch {
      setLanguageState(DEFAULT_LANGUAGE);
      applyLanguageToDOM(DEFAULT_LANGUAGE);
    } finally {
      setMounted(true);
    }
  }, [applyLanguageToDOM]);

  const setLanguage = useCallback(
    (newLang: Language) => {
      const validated: Language = newLang === "ro" ? "ro" : "en";
      setLanguageState(validated);
      applyLanguageToDOM(validated);

      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, validated);
      } catch {
        // Ignore localStorage write failures in private browsing
      }

      // Proactively notify user preferences endpoint if logged in (fire & forget)
      try {
        fetch("/api/user/preferences", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ language: validated }),
        }).catch(() => {});
      } catch {
        // Ignore API failures
      }
    },
    [applyLanguageToDOM]
  );

  const t = useCallback(
    (key: string, params?: TranslationParams): string => {
      const activeDict = locales[language] || en;
      const direct = getNestedValue(activeDict, key);
      if (direct !== undefined) {
        return interpolate(direct, params);
      }

      // Fallback to English if active language was Romanian and key was missing
      if (language !== "en") {
        const fallback = getNestedValue(en, key);
        if (fallback !== undefined) {
          return interpolate(fallback, params);
        }
      }

      // If missing completely, return key as fallback
      return key;
    },
    [language]
  );

  const contextValue = useMemo<LanguageContextType>(
    () => ({
      language,
      languages: SUPPORTED_LANGUAGES,
      setLanguage,
      t,
      isRomanian: language === "ro",
      isEnglish: language === "en",
    }),
    [language, setLanguage, t]
  );

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (!context) {
    // Graceful fallback if invoked outside Provider
    return {
      language: DEFAULT_LANGUAGE,
      languages: SUPPORTED_LANGUAGES,
      setLanguage: () => {},
      t: (key: string, params?: TranslationParams) => {
        const val = getNestedValue(en, key);
        return val !== undefined ? interpolate(val, params) : key;
      },
      isRomanian: false,
      isEnglish: true,
    };
  }
  return context;
}

export const useTranslation = useLanguage;
